import React, { useEffect, useCallback } from 'react';
import './Lightbox.css';

interface LightboxProps {
  isOpen: boolean;
  imageSrc: string;
  imageAlt: string;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

const Lightbox: React.FC<LightboxProps> = ({
  isOpen,
  imageSrc,
  imageAlt,
  onClose,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
}) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasPrev && onPrev) {
        onPrev();
      } else if (e.key === 'ArrowRight' && hasNext && onNext) {
        onNext();
      }
    },
    [onClose, onPrev, onNext, hasPrev, hasNext]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        <button className="lightbox-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>

        {hasPrev && onPrev && (
          <button className="lightbox-nav lightbox-prev" onClick={onPrev}>
            <i className="fas fa-chevron-left"></i>
          </button>
        )}

        <img src={imageSrc} alt={imageAlt} className="lightbox-image" />

        {hasNext && onNext && (
          <button className="lightbox-nav lightbox-next" onClick={onNext}>
            <i className="fas fa-chevron-right"></i>
          </button>
        )}

        <div className="lightbox-caption">{imageAlt}</div>
      </div>
    </div>
  );
};

export default Lightbox;
